import * as THREE from 'three';
import Experience from '../Experience';
import Text from './Text';
import { getHexcolorFromString, getThreeColorFromString } from '../Utils/theme.js'; // YAML files will send strings that need to be converted

import { clamp } from 'three/src/math/MathUtils.js';
//TODO: remove once we have the glsl solution 
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

/**
* Sets up zones with lettering
*/
export default class Zone {

    constructor(id, title, x, y, width, height, color, bevel, border, fontSize, fontColor, fontColorContour) {
        this.experience = new Experience;
        this.scene = this.experience.scene;
        //default values
        this.id = id || title || '';
        this.name = title || '';
        this.x = x || 0;
        this.y = y || 0;
        this.width = width || 2;
        this.height = height || 1;
        this.bevel = bevel || 0;
        this.border = border || 0;
        this.fontSize = fontSize || 22
        // this.isInteractable = isInteractable !== undefined ? isInteractable : false;        
        this.zone = new THREE.Group();
        this.scene.add(this.zone);
        this.createMesh(this.width, this.height, getThreeColorFromString(color, 0x161616), this.bevel, this.border);
        this.createLettering(this.name, this.fontSize, getHexcolorFromString(fontColor, '#ffffff'), getHexcolorFromString(fontColorContour, '#1c1c1c65'));
        this.experience.eventBus.on('themeChange', () => {
            this.updateLettering(
                this.name,
                this.fontSize,
                getHexcolorFromString(fontColor, '#ffffff'),
                getHexcolorFromString(fontColorContour, '#1c1c1c65'),
            );
        });
        //TODO: add feature: visual feedback when card is hovering over zone and another when it is  placed down.
    }
    /** 
    * creates different types of meshs for the zone depending on the parameters given
    */
    createMesh(width, height, color, bevel, border) {
        //TODO: add metalic texture
        if (border == 0) {
            if (bevel == 0) {
                this.createRectangle(width, height, color);
            } else {
                this.createBeveledRectangle(width, height, color, bevel);
            }
        } else {
            this.createOutlinedRectangle(width, height, color, bevel, border);
        }
        //TODO: return picked threemesh style so we can modify it after init and update colors
    }
    /** 
    * simple rectangle 
    */
    createRectangle(width, height, color) {
        const geomatry = new THREE.PlaneGeometry(width, height, 5, 11); //TODO: or 0,0?        
        const material = new THREE.MeshStandardMaterial(); //weirdly double side is needed here for shadows to show...
        material.side = THREE.DoubleSide;
        material.color = color; // Assign color as reference to allow updates to the original color object
        const plane = new THREE.Mesh(geomatry, material);
        plane.receiveShadow = true;
        plane.castShadow = true;
        this.zone.add(plane);
    }

    /** 
    *  Rectangle with rounded corners build from Three.shape
    */
    createBeveledRectangle(width, height, color, bevel) {
        // Make sure the bevel never gets too large which would resulting in artefacts
        const largerSideShape = Math.min(width, height);
        const maxBevel = largerSideShape / 2;
        const radius = clamp(bevel, 0, maxBevel);
        // Create the shape
        const planeRounded = new THREE.Shape();
        planeRounded.moveTo(-width / 2 + radius, -height / 2);
        planeRounded.lineTo(width / 2 - radius, -height / 2);
        planeRounded.quadraticCurveTo(width / 2, -height / 2, width / 2, -height / 2 + radius);
        planeRounded.lineTo(width / 2, height / 2 - radius);
        planeRounded.quadraticCurveTo(width / 2, height / 2, width / 2 - radius, height / 2);
        planeRounded.lineTo(-width / 2 + radius, height / 2);
        planeRounded.quadraticCurveTo(-width / 2, height / 2, -width / 2, height / 2 - radius);
        planeRounded.lineTo(-width / 2, -height / 2 + radius);
        planeRounded.quadraticCurveTo(-width / 2, -height / 2, -width / 2 + radius, -height / 2);

        const geometry = new THREE.ShapeGeometry(planeRounded);
        const material = new THREE.MeshStandardMaterial();
        material.side = THREE.DoubleSide;
        material.color = color; // Assign color as reference to allow updates to the original color object
        const plane = new THREE.Mesh(geometry, material);
        plane.receiveShadow = true;
        plane.castShadow = true;
        this.zone.add(plane);
    }

    //TODO: way to comlicated, probably better with custom glsl
    //will use the bevel to create corner points but won't smooth them, due to how we draw the spline here. Needs a more complicated solution for smooth edges
    //but also adds yet another style
    /** 
    *  Outlined Rectangle with rounded corners build from Three.Line2. Will be probably replaced
    */
    createOutlinedRectangle(width, height, color, bevel, border) {
        const largerSideShape = Math.min(width, height);
        const maxBevel = largerSideShape / 2;
        const radius = clamp(bevel, 0, maxBevel);

        // Create the shape points
        const points = [];
        points.push(new THREE.Vector2(-width / 2 + radius, -height / 2));
        points.push(new THREE.Vector2(width / 2 - radius, -height / 2));
        points.push(new THREE.Vector2(width / 2, -height / 2 + radius));
        points.push(new THREE.Vector2(width / 2, height / 2 - radius));
        points.push(new THREE.Vector2(width / 2 - radius, height / 2));
        points.push(new THREE.Vector2(-width / 2 + radius, height / 2));
        points.push(new THREE.Vector2(-width / 2, height / 2 - radius));
        points.push(new THREE.Vector2(-width / 2, -height / 2 + radius));
        points.push(new THREE.Vector2(-width / 2 + radius, -height / 2));

        // Convert points to a flat array for LineGeometry
        const flatPoints = points.flatMap(p => [p.x, p.y, 0]);

        const geometry = new LineGeometry();
        geometry.setPositions(flatPoints);
        // let color2 = new THREE.Color(.0, .0, .0);
        const material = new LineMaterial({
            color: color,
            linewidth: border,
            dashed: false,
        });
        // material.side = THREE.DoubleSide; //!seems to be bugy in threejs. Can't have shadows?

        // Create the line (outline)
        const outline = new Line2(geometry, material);
        outline.receiveShadow = true;
        outline.castShadow = true;
        this.zone.add(outline);

    }

    /** 
    *  Outlined Rectangle needs to be updated when the resolution changed
    */
    updateLineWidthFromScreenRes(data) {
        //triggered in Space class when window is resized
        this.zone.traverse((child) => {
            if (child instanceof Line2 && child.material instanceof LineMaterial) {
                child.material.resolution.set(data.detail.width, data.detail.height);
                // Adjust linewidth dynamically based on window width
                const newLineWidth = Math.max(0.5, data.detail.width / 50); // Adjust the divisor as needed
                child.material.linewidth = newLineWidth;
            }
        });
    }
    /** 
        * Creates canvas element to place custom text and then extracts a texture from it 
        */
    createLettering(title, fontSize = 22, fontColor = '#ffffff', fontColorContour = '#141414') {
        // Create the text texture and get its aspect ratio
        const { texture, aspect } = Text.createTextTexture(title, fontSize, fontColor, fontColorContour);
        texture.colorSpace = THREE.SRGBColorSpace; //usually on by default but needs to be set for custom textures
        // Match plane geomatry to texture aspect ratio
        // the fontsize determines the texture size so we factor it for the aspect ratio
        const planeHeight = fontSize / 100;
        const planeWidth = planeHeight * aspect;

        const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
        const material = new THREE.MeshStandardMaterial();
        material.map = texture;
        material.transparent = true;

        this.textPlane = new THREE.Mesh(geometry, material);
        this.textPlane.receiveShadow = true;
        this.textPlane.position.z = 0.006; //TODO: get this value from space or table
        // this.textPlane.position.y = -this.height / 2 + fontSize / 100; //add the plane to the bottom of the zone with padding
        this.textPlane.position.y = +this.height / 2 - fontSize / 80; //add the plane to the bottom of the zone with padding
        this.zone.add(this.textPlane);
    }
    updateLettering(title, fontSize = 22, fontColor = '#ffffff', fontColorContour = '#141414') {
        const { texture, aspect } = Text.createTextTexture(title, fontSize, fontColor, fontColorContour);
        texture.colorSpace = THREE.SRGBColorSpace; //usually on by default but needs to be set for custom textures        
        if (this.textPlane.material.map) {
            this.textPlane.material.map.dispose();
        }
        this.textPlane.material.map = texture;
        this.textPlane.material.needsUpdate = true;
    }


    /** 
    * Assign positions for zones 
    */
    place(position) {
        if (!(position instanceof THREE.Vector3)) {
            position = new THREE.Vector3(0, 0, 0.01);
        } else {
            this.zone.position.copy(position);
        }

    }

    containsPoint(point) {
        if (!point) return false;
        const zonePosition = this.zone.position;
        return Math.abs(point.x - zonePosition.x) <= this.width / 2
            && Math.abs(point.y - zonePosition.y) <= this.height / 2;
    }

    addTexture(pathToImage) {

    }

}
